/* eslint-disable @typescript-eslint/no-explicit-any */
import { forwardRef } from 'react';
import type {
  ReactElement,
  ReactNode,
  Ref,
} from 'react';

import {
  createFilter,
  GroupBase,
} from 'react-select';
import { FilterOptionOption } from 'react-select/dist/declarations/src/filters';

import { SelectBase } from '../SelectBase';

import { texts } from '../ReactSelectExtended/texts';
import {
  useStyles,
} from '../ReactSelectExtended/styles';
import { selectComponentsSync } from '../ReactSelectExtended/components';
import type {
  OptionTypeBase,
  SelectBaseProps,
} from '../ReactSelectExtended/types';

import { ReactSelectExtended } from '../ReactSelectExtended/ReactSelectExtended';
import type {
  ReactSelectExtendedProps,
} from '../ReactSelectExtended/ReactSelectExtended';

export type SelectProps<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
> =
  & ReactSelectExtendedProps<OptionType, IsMulti, Group>
  & Omit<SelectBaseProps<OptionType, Group, IsMulti>, 'defaultComponents' | 'children'>;

const filterOption = createFilter({
  stringify: (option: FilterOptionOption<OptionTypeBase>) => option.label,
});

function SelectComponent<
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>(
  props: SelectProps<OptionType, Group, IsMulti>,
  ref: Ref<HTMLDivElement>,
): ReactElement {
  const {
    hasError = false,
    hasWarning = false,
    isMulti = false,
    isClearable = true,
    small = false,
    renderModal = undefined,
    selectRef = null,
    ...restProps
  } = props;

  const styles = useStyles(small);

  return (
    <SelectBase
      defaultComponents={selectComponentsSync}
      {...props}
      ref={ref}
    >
      {({
        getOptionLabel,
        getOptionValue,
        components,
      }): ReactNode => (
        <ReactSelectExtended
          {...texts}
          isClearable={isClearable}
          isMulti={isMulti}
          hasError={hasError}
          hasWarning={hasWarning}
          small={small}
          closeMenuOnSelect={!isMulti}
          isArrowHidden={Boolean(renderModal)}
          components={components}
          styles={styles}
          filterOption={filterOption}
          {...restProps}
          getOptionLabel={getOptionLabel}
          getOptionValue={getOptionValue}
          ref={selectRef}
        />
      )}
    </SelectBase>
  );
}

export const Select = forwardRef(SelectComponent) as <
OptionType extends OptionTypeBase,
Group extends GroupBase<OptionType>,
IsMulti extends boolean,
>(props: SelectProps<OptionType, Group, IsMulti> & {
  ref?: Ref<HTMLDivElement>;
}) => ReactElement;
